// Catalog.ID Client — Booklet Credentials Page
// ============================================================================
// The member's paper credential record (successor of the "Identity Document",
// registration-restructure §5 page 2): password, supersecret, and the contact
// channels submitted at registration. Deliberately absent (decided 2026-09-01):
// the key inventory (no scenario needs it on paper), the CID QR (typeable
// payload), "passport" framing, and any checksum marking — the supersecret's
// per-line checksum characters print like every other character and validate
// silently on re-entry.
//
// The organization variant carries the supersecret only: the org password is a
// never-disclosed internal throwaway, and the page explains that day-to-day
// access always goes through delegate admin accounts — and, for the same
// reason, skips the "How authentication works" section (no org password, no
// org PIN; there is no routine organization login to explain).

import type { Content, TableCell } from 'pdfmake/interfaces';
import { parseSupersecret } from '../../crypto/supersecret';
import { accountHeader, type AccountHeaderData } from './shared';

export interface CredentialsPageData {
    header: AccountHeaderData;
    /** Absent on the organization variant. */
    password?: string;
    /** Four-line extended supersecret (display form). Absent on a Settings
     *  reprint — secrets exist only in the original registration booklet. */
    supersecret?: string;
    /** Contact channels as submitted at registration (personal accounts). */
    email?: string;
    phone?: string;
    /** Organization credentials: no password section, emergency-use explanation. */
    organizationVariant?: boolean;
    /** Present when the kit also carries Account Recovery Sheets: the
     *  irrecoverability warning then names them as the exception. */
    recoveryKit?: { thresholdK: number; totalSharesN: number };
}

export function buildCredentialsPage(data: CredentialsPageData): Content[] {
    const content: Content[] = [
        { text: 'Credentials', style: 'pageTitle' },
        accountHeader(data.header),
    ];

    if (data.organizationVariant) {
        content.push({
            text: 'This organization supersecret exists for emergencies only — for example, '
                + 'replacing the organization\'s keys after a compromise. Day-to-day administration '
                + 'always happens through the organization\'s delegate admin accounts; there is no '
                + 'routine organization login.',
            style: 'value',
            margin: [0, 0, 0, 14],
        });
    } else if (data.password !== undefined) {
        content.push({
            unbreakable: true,
            stack: [
                { text: 'Password', style: 'section' },
                { text: data.password, style: 'credential', margin: [0, 2, 0, 2] },
                { text: 'Used together with the account id above to log in.', style: 'note' },
            ],
            margin: [0, 0, 0, 14],
        });
    }

    if (data.supersecret !== undefined) {
        content.push({
            unbreakable: true,
            stack: [
                { text: 'Supersecret', style: 'section' },
                supersecretBlock(data.supersecret),
                {
                    text: 'The supersecret opens the account\'s keys. It is not stored '
                        + 'anywhere and cannot be recovered if lost.',
                    style: 'note',
                    margin: [0, 6, 0, 0],
                },
            ],
            margin: [0, 0, 0, 14],
        });
    } else if (!data.organizationVariant && data.password === undefined) {
        // Settings reprint: the secrets exist only on the original booklet.
        content.push({
            text: 'The password and supersecret are not stored anywhere and therefore cannot be '
                + 'reprinted — they appear only in the kit from the original registration. If the '
                + 'supersecret is lost, have the account issue new keys and a new supersecret from a '
                + 'device that is still logged in.',
            style: 'value',
            margin: [0, 0, 0, 14],
        });
    }

    if (data.email || data.phone) {
        const rows: TableCell[][] = [];
        if (data.email) rows.push([{ text: 'Email', style: 'label' }, { text: data.email, style: 'value' }]);
        if (data.phone) rows.push([{ text: 'Phone', style: 'label' }, { text: data.phone, style: 'value' }]);
        content.push({
            unbreakable: true,
            stack: [
                { text: 'Contact Channels', style: 'section' },
                { layout: 'noBorders', table: { widths: [110, '*'], body: rows } },
                {
                    text: 'Submitted and verified at registration; the Account Unlock Sheet sends its codes here.',
                    style: 'note',
                    margin: [0, 4, 0, 0],
                },
            ],
            margin: [0, 0, 0, 14],
        });
    }

    if (!data.organizationVariant) content.push(authenticationSection());

    content.push({
        unbreakable: true,
        stack: [
            { text: 'Keep This Sheet Safe', style: 'section' },
            {
                ul: [
                    'Anyone holding this sheet can take over the identity.',
                    'Keep it in a secure location; do not photograph it or store scans of it.',
                    data.recoveryKit
                        ? 'If the supersecret is lost and no device still holds the keys, the identity is '
                            + 'irrecoverable by design — except through the Account Recovery Sheets in this kit: '
                            + `any ${data.recoveryKit.thresholdK} of the ${data.recoveryKit.totalSharesN} sheets `
                            + 'recreate the supersecret.'
                        : 'If the supersecret is lost and no device still holds the keys, the identity is irrecoverable by design.',
                ],
                style: 'note',
            },
        ],
    });

    return content;
}

/**
 * Why there are three secrets and what each one actually does (review
 * 2026-09-02, third round). Members conflate them: the page's job is to make
 * clear that the supersecret is the key, the password is only the door, and the
 * PIN never leaves the device — so a stolen password alone opens nothing and a
 * stolen device alone opens nothing. Personal pages only.
 */
function authenticationSection(): Content {
    return {
        unbreakable: true,
        stack: [
            { text: 'How authentication works', style: 'section' },
            {
                text: 'Supersecret — entered once on each new device or browser, where it unlocks your '
                    + 'keys. After that it is needed again only for recovery, or when the account issues new keys. The '
                    + 'software never stores it.',
                style: 'note',
                margin: [0, 0, 0, 4],
            },
            {
                text: 'Password — proves it is you at login; a session then stays open until you log out '
                    + 'or it expires. Your browser or password manager may remember it.',
                style: 'note',
                margin: [0, 0, 0, 4],
            },
            {
                text: 'PIN — unlocks the keys kept on a device at the start of each session on that '
                    + 'device. The PIN itself is stored nowhere — not on the device, not in a password '
                    + 'manager. The server can only check whether a PIN is right, and only for that one '
                    + 'device, so the PIN is useless without it.',
                style: 'note',
                margin: [0, 0, 0, 4],
            },
        ],
        margin: [0, 0, 0, 14],
    };
}

/**
 * The four-line supersecret as a table: one row per line, one cell per
 * dash-group. Every character prints identically — the per-line checksum
 * character is intentionally NOT set off (it confused members and can become
 * unreadable on a degraded print; validation happens electronically on entry).
 */
function supersecretBlock(supersecret: string): Content {
    const { lineErrors } = parseSupersecret(supersecret);
    if (lineErrors.length > 0) {
        // Not the four-line extended format (e.g. a legacy value) — print verbatim.
        return { text: supersecret, style: 'credential', preserveLeadingSpaces: true };
    }

    const groupStyle = { fontSize: 14, bold: true, characterSpacing: 2 } as const;
    const cleaned = supersecret.replace(/[-\s]/g, '');
    const body: TableCell[][] = [];
    for (let line = 0; line < 4; line++) {
        const chars = cleaned.slice(line * 12, (line + 1) * 12);
        body.push([
            { text: `${line + 1}`, fontSize: 9, margin: [0, 5, 0, 0] },
            { text: chars.slice(0, 4), ...groupStyle },
            { text: chars.slice(4, 8), ...groupStyle },
            { text: chars.slice(8, 12), ...groupStyle },
        ]);
    }
    return {
        layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 2,
            paddingBottom: () => 2,
        },
        table: { widths: ['auto', 'auto', 'auto', 'auto'], body },
    };
}
